import { useState, useCallback, useRef, useEffect } from 'react';
import {
  BoxSection,
  ButtonPrimary,
  ConfirmModal,
  Spinner,
} from '../../../../../../app/components';
import { TRegisterBusinessDataRequest } from '../../../../context';
import { useTheme } from '@mui/material';
import { ButtonArea, TableWrapper } from './styles';
import { Table, TextM, TextS } from 'app/components';
import { EmptyArea } from 'modules/management/components';
import { useCustomerService } from 'modules/customer/services';
import { formatCNPJ } from 'app/helpers';
import {
  TScrDetailItem,
  TScrListResponse,
} from 'modules/customer/interfaces/scr';
import { AddOutlined } from '@mui/icons-material';
import {
  TableBodyCellContainer,
  TableHeadCellContainer,
} from 'app/styles/components/Table';
import { formatCurrency } from 'app/utils/normalizer';
import NaturalPersonViewScrDetailsSideSheet from './LegalPersonViewScrDetailsSideSheet';
import { usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import { ExternalIcon } from 'app/components/Icons';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import {
  formatDateFromBackend,
  formatStringHourFromBackend,
} from 'app/helpers/dateHelpers';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { ScrNotAuthorized } from 'app/components/Icons/ScrNotAuthorized';
import { ScrRequestIcon } from 'app/components/Icons/ScrRequestIcon';
import { notification } from 'antd';
import { useOriginatorContext } from 'modules/common/OriginatorProvider';
import FileUploadIcon from 'app/components/Icons/FileUploadIcon';
import QuillPenIcon from 'app/components/Icons/QuillPenIcon';
import SrcEmptyState from 'app/components/Icons/svgs/FileNotFound.svg';

type ILegalPersonViewScrTabProps = {
  business: TRegisterBusinessDataRequest;
};

export async function fileToHash(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

const tableHeader = [
  {
    id: '1',
    content: (
      <TableHeadCellContainer>
        <TextS weight="Bold">Data base consultada</TextS>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '2',
    content: (
      <TableHeadCellContainer>
        <TextS weight="Bold">Data da consulta</TextS>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '3',
    content: (
      <TableHeadCellContainer>
        <TextS weight="Bold">Coobrigação assumida</TextS>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '4',
    content: (
      <TableHeadCellContainer>
        <TextS weight="Bold">Ação</TextS>
      </TableHeadCellContainer>
    ),
  },
];

const LegalPersonViewScrTab = ({ business }: ILegalPersonViewScrTabProps) => {
  /* Hooks */
  const [api, context] = notification.useNotification();
  const theme = useTheme();
  const { getOriginator, isLoadingOriginator } = useOriginatorContext();
  const {
    getBusinessScrList,
    requestBusinessScr,
    requestAcceptanceTermsScr,
    getBusinessScrLoading,
    requestBusinessScrLoading,
    requestAcceptanceTermsScrLoading,
  } = useCustomerService();
  const navigate = useNavigate();
  const { isProfileAuthorized } = usePermify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* States */
  const [hasScrEvidence, setHasScrEvidence] = useState(
    business?.has_scr_evidence,
  );
  const [isScrObrigatory, setIsScrObrigatory] = useState(false);
  const [isConfirmRequestScrOpen, toggleConfirmRequestModalOpen] =
    useState(false);
  const [isAcceptanceTermsScrOpen, setAcceptanceTermsModalOpen] =
    useState(false);
  const [linesPerPage, setLinesPerPage] = useQueryParam(
    'size',
    withDefault(NumberParam, 10),
  );
  const [totalItemsQty, setTotalItemsQty] = useState(0);
  const [currentPage, setCurrentPage] = useQueryParam(
    'page',
    withDefault(NumberParam, 0),
  );
  const [scrDetailItem, updateScrDetailItem] = useState<TScrDetailItem | null>(
    null,
  );
  const [scrList, updateScrList] = useState<TScrListResponse[]>();
  const [currentStep, setCurrentStep] = useState<
    'TERMS' | 'NO_DATA' | 'DATA' | 'LOADING'
  >('LOADING');

  /* Handlers */
  const fetchScrList = (page: number, itemsPerPage?: number) => {
    getBusinessScrList(business.id as string, page, itemsPerPage).then(
      (scrListResponse) => {
        if (scrListResponse) {
          updateScrList(
            scrListResponse.content.map((scr) => ({
              created_at: scr.created_at,
              id: scr.id,
              requested_date: scr.requested_date,
              result: scr.result,
            })),
          );
          setTotalItemsQty(scrListResponse.total_elements);
        }
      },
    );
  };

  const onRequestScr = useCallback(() => {
    requestBusinessScr(business.id as string).then((scrResponse) => {
      if (scrResponse) fetchScrList(0);
      toggleConfirmRequestModalOpen(false);
    });
  }, [business]);

  const onRequestAcceptanceTermsScr = useCallback(async () => {
    const res = await requestAcceptanceTermsScr(business.id as string);
    setAcceptanceTermsModalOpen(false);

    if (res) {
      api.success({
        message: `Termo de aceite enviado com sucesso para o e-mail ${business.email_address}.`,
      });
    } else {
      api.error({
        message: `Erro ao enviar termo para o e-mail ${business.email_address}.`,
      });
    }
  }, [business]);

  const getOriginatorHandler = async (originatorId: string) => {
    const originator = await getOriginator(originatorId);

    setIsScrObrigatory(originator?.require_scr_consent ?? false);
  };

  const uploadFileHandler = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (files && files.length > 0) {
        const file = files[0];

        if (Number((file.size / (1024 * 1024)).toFixed(2)) > 20) {
          console.error(
            `Arquivo inválido. ${file.name} possui tamanho superior a 20MB`,
          );
          api.error({
            message: `Arquivo inválido. ${file.name} possui tamanho superior a 20MB`,
            duration: 1000,
            placement: 'topRight',
          });
          return;
        }

        try {
          const hash = await fileToHash(file);
          const result = await requestAcceptanceTermsScr(
            business.id as string,
            hash,
          );

          if (result) {
            api.success({
              message: 'Upload realizado com sucesso',
              placement: 'topRight',
            });

            setHasScrEvidence(true);
            setCurrentStep('NO_DATA');

            return;
          }

          api.error({
            message: 'Erro ao realizar upload do termo SCR',
            duration: 1000,
            description:
              'Se você já enviou o termo, aguarde a autorização. Caso o erro persista, entre em contato com o suporte.',
            placement: 'topRight',
          });
        } catch (error) {
          console.error('Erro no upload do termo SCR:', error);
          api.error({
            message: 'Erro ao realizar upload do termo SCR',
            duration: 1000,
            description:
              'Se você já enviou o termo, aguarde a autorização. Caso o erro persista, entre em contato com o suporte.',
            placement: 'topRight',
          });
        }
      }

      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [business],
  );

  const redirectHandler = useCallback(() => {
    if (isScrObrigatory && !hasScrEvidence) {
      return setCurrentStep('TERMS');
    }

    if (!scrList || scrList.length === 0) {
      return setCurrentStep('NO_DATA');
    }

    return setCurrentStep('DATA');
  }, [isScrObrigatory, hasScrEvidence, scrList]);

  const steps = {
    ['TERMS']: (
      <EmptyArea
        message="Termo de aceite não autorizado"
        description={`Para gerar o SCR, é necessário que o Termo de Aceite 
esteja devidamente autorizado. O termo será enviado para o e-mail ${business?.email_address}.`}
        isLoading={requestAcceptanceTermsScrLoading}
        customButton={
          <ButtonPrimary
            style={{ maxWidth: '265px' }}
            typeVariant="fill"
            iconPosition="right"
            onClick={onRequestAcceptanceTermsScr}
            btnIcon={
              <QuillPenIcon color={theme.palette.common.white as string} />
            }
          >
            <TextM
              weight="Semibold"
              style={{
                marginRight: '11px',
                color: theme.palette.common.white,
              }}
            >
              Enviar termo para assinatura
            </TextM>
          </ButtonPrimary>
        }
      >
        <ScrNotAuthorized />
      </EmptyArea>
    ),
    ['NO_DATA']: (
      <EmptyArea
        message="Nenhuma consulta de SCR realizada"
        isLoading={getBusinessScrLoading}
        description="Gere um SCR para ver os resultados aqui."
      >
        <img src={SrcEmptyState} />
      </EmptyArea>
    ),
    ['DATA']: (
      <TableWrapper>
        <Table
          tableHeadCell={tableHeader}
          showPagination
          handlePageChange={(selectedPage) => {
            setCurrentPage(selectedPage - 1);
            fetchScrList(selectedPage - 1, linesPerPage);
          }}
          handleLinesPerPage={(selectedLinesPerPage) => {
            setCurrentPage(0);
            setLinesPerPage(
              selectedLinesPerPage === 0 ? 10 : selectedLinesPerPage,
            );
          }}
          totalElementsQty={totalItemsQty}
          currentPage={currentPage + 1}
          linesPerPage={linesPerPage}
          tableBodyContent={scrList?.map((scr, index) => {
            const baseDateString =
              scr.result.dados.dataBaseConsultada?.split('-');
            const baseDate = baseDateString
              ? new Date(
                  parseInt(baseDateString[0]),
                  parseInt(baseDateString[1]) - 1,
                  1,
                )
              : new Date();
            return {
              id: `${scr.result.dados.dataBaseConsultada}-${String(index)}`,
              cells: [
                {
                  id: '1',
                  content: (
                    <TableBodyCellContainer>
                      <TextS weight="Regular">
                        {`${format(baseDate, 'MMM', {
                          locale: ptBR,
                        }).toUpperCase()} ${format(baseDate, 'yyyy', {
                          locale: ptBR,
                        })}`}
                        {}
                      </TextS>
                    </TableBodyCellContainer>
                  ),
                },
                {
                  id: '2',
                  content: (
                    <TableBodyCellContainer>
                      <TextS>{`${formatDateFromBackend(
                        scr?.created_at || '',
                      ).toLocaleDateString('pt-BR')} às
                              ${formatStringHourFromBackend(
                                scr?.created_at || '',
                              )}`}</TextS>
                    </TableBodyCellContainer>
                  ),
                },
                {
                  id: '3',
                  content: (
                    <TableBodyCellContainer>
                      <TextS>
                        {scr.result?.dados?.coobrigacaoAssumida
                          ? formatCurrency(
                              parseFloat(scr.result.dados.coobrigacaoAssumida),
                            )
                          : '-'}
                      </TextS>
                    </TableBodyCellContainer>
                  ),
                },
                {
                  id: '4',
                  content: (
                    <TableBodyCellContainer>
                      <Link
                        to={`/records/legal/${business.id}/scr/${scr.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '14px',
                          color: theme.palette.brand.primary.base as string,
                          textDecoration: 'underline',
                        }}
                      >
                        Acessar
                        <span
                          style={{
                            paddingLeft: '10px',
                          }}
                        >
                          <ExternalIcon
                            color={theme.palette.brand.primary.base as string}
                            width={9}
                            height={9}
                          />
                        </span>
                      </Link>
                    </TableBodyCellContainer>
                  ),
                },
              ],
            };
          })}
        />
      </TableWrapper>
    ),
    ['LOADING']: (
      <EmptyArea message="" isLoading={true}>
        {null}
      </EmptyArea>
    ),
  };

  useEffect(() => {
    if (isProfileAuthorized(EAccountPermissions.READ_SCR)) {
      fetchScrList(0);
    } else {
      navigate('/');
    }

    getOriginatorHandler(business.originator?.id as string);
    redirectHandler();
  }, [business]);

  useEffect(() => {
    redirectHandler();
  }, [scrList]);

  return (
    <BoxSection level="wrapper">
      {context}
      {isLoadingOriginator && <Spinner fullscreen />}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          height: 'fit-content',
        }}
      >
        <ButtonArea style={{ marginBottom: '0px' }}>
          <ButtonPrimary
            style={{ maxWidth: '13rem' }}
            iconPosition="right"
            typeVariant="outline"
            btnIcon={
              <FileUploadIcon
                color={theme.palette.brand.primary.base as string}
              />
            }
            onClick={uploadFileHandler}
          >
            <TextM
              weight="Semibold"
              style={{
                marginRight: '11px',
                color: theme.palette.brand.primary.base as string,
              }}
            >
              Upload do termo
            </TextM>
          </ButtonPrimary>
          {currentStep !== 'TERMS' && (
            <ButtonPrimary
              style={{ maxWidth: '20rem', marginLeft: '1.5rem' }}
              iconPosition="right"
              typeVariant="outline"
              btnIcon={
                <QuillPenIcon
                  color={theme.palette.brand.primary.base as string}
                />
              }
              onClick={onRequestAcceptanceTermsScr}
            >
              <TextM
                weight="Semibold"
                style={{
                  marginRight: '11px',
                  color: theme.palette.brand.primary.base as string,
                }}
              >
                Enviar termo para assinatura
              </TextM>
            </ButtonPrimary>
          )}

          {isProfileAuthorized(EAccountPermissions.CREATE_SCR) && (
            <ButtonPrimary
              style={{
                maxWidth: '136px',
                height: '48px',
                padding: '12px',
                marginLeft: '1.5rem',
              }}
              disabled={isScrObrigatory && !hasScrEvidence}
              onClick={() => {
                if (hasScrEvidence) {
                  toggleConfirmRequestModalOpen(true);
                  return;
                }
                setAcceptanceTermsModalOpen(true);
              }}
            >
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.common.white }}
              >
                Gerar SCR
              </TextM>
              <AddOutlined
                style={{
                  color: theme.palette.common.white as string,
                  marginLeft: '5px',
                  fontSize: '22px',
                }}
              />
            </ButtonPrimary>
          )}
        </ButtonArea>
      </div>

      {steps[currentStep]}

      <ConfirmModal
        isOpen={isAcceptanceTermsScrOpen}
        title="Termo de aceite não autorizado"
        color="primary"
        width="390px"
        style={{
          padding: '24px 0px',
          minWidth: '470px',
        }}
        btnConfirmCustom={
          <ButtonPrimary
            typeVariant="fill"
            style={{
              width: '120px',
            }}
            onClick={() => setAcceptanceTermsModalOpen(false)}
          >
            <TextM
              weight="Semibold"
              style={{
                color: theme.palette.common.white as string,
              }}
            >
              Ok
            </TextM>
          </ButtonPrimary>
        }
        btnCancelCustom={
          <ButtonPrimary
            style={{ maxWidth: '265px' }}
            typeVariant="ghost"
            iconPosition="right"
            btnIcon={
              <ScrRequestIcon
                color={theme.palette.brand.primary.base as string}
              />
            }
            onClick={onRequestAcceptanceTermsScr}
          >
            <TextM
              weight="Semibold"
              style={{
                marginRight: '11px',
                color: theme.palette.brand.primary.base as string,
              }}
            >
              Enviar termo novamente
            </TextM>
          </ButtonPrimary>
        }
        isLoading={requestAcceptanceTermsScrLoading}
        handleClose={() => setAcceptanceTermsModalOpen(false)}
      >
        Para gerar o SCR, é necessário que o Termo de Aceite esteja devidamente
        autorizado.
      </ConfirmModal>
      <ConfirmModal
        isOpen={isConfirmRequestScrOpen}
        title="A realização desta consulta gera custo para o Originador."
        color="primary"
        btnConfirmText="Confirmar"
        isLoading={requestBusinessScrLoading}
        handleConfirm={onRequestScr}
        handleClose={() => toggleConfirmRequestModalOpen(false)}
        handleCancel={() => toggleConfirmRequestModalOpen(false)}
      >
        Para consultar o SCR é obrigatório que o titular do CPF{' '}
        <strong>{formatCNPJ(business.taxpayer_id)}</strong> tenha autorizado
        previamente a consulta, de forma livre e consciente. Você confirma que
        obteve essa autorização?
      </ConfirmModal>
      <NaturalPersonViewScrDetailsSideSheet
        handleClose={() => updateScrDetailItem(null)}
        isOpen={scrDetailItem !== null}
        itemDetail={scrDetailItem}
      />

      {/* Input de arquivo oculto para upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </BoxSection>
  );
};

export default LegalPersonViewScrTab;
